import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, MessageCircle } from 'lucide-react';
import { useContact } from '@/context/ContactContext';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { contactSettings } = useContact();

  const propertyTypes = [
    { name: 'Office Space', path: '/properties/office' },
    { name: 'Retail Shops', path: '/properties/retail' },
    { name: 'Mall Shops', path: '/properties/mall_shop' },
    { name: 'SCO Plots', path: '/properties/sco' },
    { name: 'Commercial Plots', path: '/properties/commercial_plot' },
    { name: 'Co-working Space', path: '/properties/coworking' },
  ];

  const popularSectors = [
    'Golf Course Road',
    'Cyber City (Sector 24-25)',
    'MG Road',
    'Sector 29',
    'Dwarka Expressway',
    'Sohna Road',
  ];

  return (
    <footer style={{ background: 'linear-gradient(180deg, #1c1c1e, #0f0f10)' }} className="text-white" data-testid="footer">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div>
            <Link to="/" className="inline-block mb-6">
              <img 
                src="/images/urbancial-logo.png" 
                alt="URBANCIAL - Commercial Properties" 
                className="h-20 w-auto brightness-0 invert"
                style={{ filter: 'brightness(0) invert(1) sepia(1) saturate(3) hue-rotate(15deg)' }}
              />
            </Link>
            <p className="text-sm leading-relaxed mb-6" style={{ color: 'rgba(201,168,76,0.5)', fontFamily: "'Tenor Sans', sans-serif" }}>
              Your trusted partner for premium commercial real estate in Gurgaon. 
              We specialize in office spaces, retail shops, SCO plots, and more.
            </p>
            <div className="flex gap-4">
              <a
                href={`https://wa.me/${contactSettings.whatsapp_number?.replace(/[^0-9]/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center transition-colors duration-300 rounded-lg"
                style={{ background: 'rgba(201,168,76,0.1)', color: '#c9a84c' }}
                data-testid="whatsapp-link"
              >
                <MessageCircle className="w-5 h-5" />
              </a>
              <a
                href={`tel:${contactSettings.phone_primary?.replace(/\s/g, '')}`}
                className="w-10 h-10 flex items-center justify-center transition-colors duration-300 rounded-lg"
                style={{ background: 'rgba(201,168,76,0.1)', color: '#c9a84c' }}
                data-testid="phone-footer-link"
              >
                <Phone className="w-5 h-5" />
              </a>
              <a
                href={`mailto:${contactSettings.email_primary}`}
                className="w-10 h-10 flex items-center justify-center transition-colors duration-300 rounded-lg"
                style={{ background: 'rgba(201,168,76,0.1)', color: '#c9a84c' }}
                data-testid="email-link"
              >
                <Mail className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Property Types */}
          <div>
            <h4 className="text-sm uppercase tracking-widest font-bold mb-6" style={{ color: '#c9a84c', fontFamily: "'Josefin Sans', sans-serif" }}>
              Property Types
            </h4>
            <ul className="space-y-3">
              {propertyTypes.map((type) => (
                <li key={type.path}>
                  <Link to={type.path} className="text-sm transition-colors duration-300" style={{ color: 'rgba(201,168,76,0.5)', fontFamily: "'Tenor Sans', sans-serif" }}
                    onMouseEnter={(e) => { e.target.style.color = '#c9a84c'; }}
                    onMouseLeave={(e) => { e.target.style.color = 'rgba(201,168,76,0.5)'; }}>
                    {type.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Popular Sectors */}
          <div>
            <h4 className="text-sm uppercase tracking-widest font-bold mb-6" style={{ color: '#c9a84c', fontFamily: "'Josefin Sans', sans-serif" }}>
              Popular Locations
            </h4>
            <ul className="space-y-3">
              {popularSectors.map((sector) => (
                <li key={sector}>
                  <Link to="/properties" className="text-sm transition-colors duration-300" style={{ color: 'rgba(201,168,76,0.5)', fontFamily: "'Tenor Sans', sans-serif" }}
                    onMouseEnter={(e) => { e.target.style.color = '#c9a84c'; }}
                    onMouseLeave={(e) => { e.target.style.color = 'rgba(201,168,76,0.5)'; }}>
                    {sector}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-sm uppercase tracking-widest font-bold mb-6" style={{ color: '#c9a84c', fontFamily: "'Josefin Sans', sans-serif" }}>
              Contact Us
            </h4>
            <div className="space-y-4">
              <div className="flex gap-3">
                <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#c9a84c' }} />
                <p className="text-sm" style={{ color: 'rgba(201,168,76,0.5)', fontFamily: "'Tenor Sans', sans-serif" }}>
                  {contactSettings.address_line1}<br />
                  {contactSettings.address_line2}
                </p>
              </div>
              <div className="flex gap-3">
                <Phone className="w-5 h-5 flex-shrink-0" style={{ color: '#c9a84c' }} />
                <a href={`tel:${contactSettings.phone_primary?.replace(/\s/g, '')}`} className="text-sm transition-colors" style={{ color: 'rgba(201,168,76,0.5)' }}>
                  {contactSettings.phone_primary}
                </a>
              </div>
              <div className="flex gap-3">
                <Mail className="w-5 h-5 flex-shrink-0" style={{ color: '#c9a84c' }} />
                <a href={`mailto:${contactSettings.email_primary}`} className="text-sm transition-colors" style={{ color: 'rgba(201,168,76,0.5)' }}>
                  {contactSettings.email_primary}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div style={{ borderTop: '1px solid rgba(201,168,76,0.1)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs" style={{ color: 'rgba(201,168,76,0.35)', fontFamily: "'Tenor Sans', sans-serif" }}>
              {currentYear} URBANCIAL. All rights reserved.
            </p>
            <div className="flex gap-6">
              <span
                className="text-xs cursor-default select-none px-3 py-1 rounded"
                style={{ color: '#c9a84c', border: '1px solid rgba(201,168,76,0.3)' }}
                onClick={(e) => { if (e.detail === 3) window.location.href = '/admin/login'; }}
                data-testid="rera-registered"
              >
                RERA Registered
              </span>
            </div>
          </div>
          <p className="text-[10px] mt-4 text-center md:text-left" style={{ color: 'rgba(201,168,76,0.25)', fontFamily: "'Tenor Sans', sans-serif" }}>
            Disclaimer: This website is for information purposes only. All property details, images, and pricing are subject to change without notice. 
            Please verify all details with the developer before making any investment decisions.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
